import { STORAGE_KEYS } from '../components/dynamic-inputs/util/constants';

const BUILD_VERSION_KEY = 'kappta_web_build';
const INDEXED_DB_NAME = 'Capana';
const APP_STATE_STORE = 'app_state';
const IMAGES_STORE = 'images';
const POLL_INTERVAL_MS = 3 * 60 * 1000;

/** Rotas fora do funil ativo — só nessas o poll aplica limpeza/reload. */
const OUTSIDE_FUNNEL_PATHS = new Set([
  '',
  'init',
  'enviado',
  'aprovado',
  'produtos',
  'reanalise',
]);

export interface AppVersionInfo {
  version: string;
}

export type FreshBuildOutcome = 'up-to-date' | 'reloading' | 'deferred' | 'skipped';

export interface EnsureFreshBuildOptions {
  /** When false, never wipe/reload (used by poll while inside funnel routes). */
  allowWipeReload?: boolean;
}

let ensureFreshPromise: Promise<FreshBuildOutcome> | null = null;
let deferredNoticeShownFor: string | null = null;
let deferredListener: ((remoteVersion: string) => void) | null = null;

/**
 * Register a UI callback when a new build is detected but wipe/reload is deferred (mid-funnel).
 * Returns an unsubscribe function.
 */
export function onBuildUpdateDeferred(
  listener: (remoteVersion: string) => void
): () => void {
  deferredListener = listener;
  return () => {
    if (deferredListener === listener) {
      deferredListener = null;
    }
  };
}

export function isOutsideFunnelRoute(url: string = location.pathname): boolean {
  const path = url.split('?')[0].replace(/^\//, '').replace(/\/$/, '');
  // first segment only (ignore tenant-like multi-segment if any)
  const first = path.split('/')[0] ?? '';
  return OUTSIDE_FUNNEL_PATHS.has(first);
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (err) {
    console.warn('[AppVersion] Failed to unregister service workers', err);
  }
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as AppVersionInfo;
    return data?.version ?? null;
  } catch (err) {
    console.warn('[AppVersion] Failed to fetch version.json', err);
    return null;
  }
}

function openCapanaDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(INDEXED_DB_NAME);
      request.onerror = () => resolve(null);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = () => {
        /* no-op */
      };
    } catch {
      resolve(null);
    }
  });
}

function idbGet(store: IDBObjectStore, key: IDBValidKey): Promise<any> {
  return new Promise((resolve) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(undefined);
  });
}

function idbCount(store: IDBObjectStore): Promise<number> {
  return new Promise((resolve) => {
    const request = store.count();
    request.onsuccess = () => resolve(request.result ?? 0);
    request.onerror = () => resolve(0);
  });
}

function isMeaningfulAppState(value: any): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  if (value.proposta?.id) {
    return true;
  }
  if (typeof value.currentStepIndex === 'number' && value.currentStepIndex > 0) {
    return true;
  }
  if (value.form && Object.keys(value.form).length > 0) {
    return true;
  }
  if (Array.isArray(value.pages) && value.pages.length > 0) {
    return true;
  }
  if (value.fieldsAsObject && Object.keys(value.fieldsAsObject).length > 0) {
    return true;
  }
  return false;
}

/**
 * True when the user appears mid-onboarding (Capana has funnel state and/or images).
 */
export async function hasActiveOnboarding(): Promise<boolean> {
  const db = await openCapanaDb();
  if (!db) {
    return false;
  }

  try {
    const storeNames = Array.from(db.objectStoreNames);

    if (storeNames.includes(IMAGES_STORE)) {
      const imageCount = await idbCount(
        db.transaction(IMAGES_STORE, 'readonly').objectStore(IMAGES_STORE)
      );
      if (imageCount > 0) {
        db.close();
        return true;
      }
    }

    if (storeNames.includes(APP_STATE_STORE)) {
      const entry = await idbGet(
        db.transaction(APP_STATE_STORE, 'readonly').objectStore(APP_STATE_STORE),
        STORAGE_KEYS.APP_STATE
      );
      const value = entry?.value ?? entry;
      if (isMeaningfulAppState(value)) {
        db.close();
        return true;
      }
    }

    db.close();
  } catch (err) {
    console.warn('[AppVersion] Failed to inspect Capana for active onboarding', err);
    try {
      db.close();
    } catch {
      /* ignore */
    }
  }

  return false;
}

/** Soft wipe: only ENV_CONFIG. Capana fica para o clearAppState do funil. */
async function wipeEnvConfig(): Promise<void> {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.ENV_CONFIG);
  } catch (err) {
    console.warn('[AppVersion] Failed to remove ENV_CONFIG', err);
  }
}

function notifyDeferred(remoteVersion: string): void {
  if (deferredNoticeShownFor === remoteVersion) {
    return;
  }
  deferredNoticeShownFor = remoteVersion;
  console.info(
    '[AppVersion] New build available, onboarding in progress — deferring wipe/reload.',
    remoteVersion
  );
  deferredListener?.(remoteVersion);
}

/**
 * Soft apply (fora do funil): limpa só ENV_CONFIG, grava SHA e recarrega.
 * Capana não é apagado aqui — o funil já chama clearAppState ao terminar/recomeçar.
 */
async function applySoftUpdate(remoteVersion: string): Promise<never> {
  await wipeEnvConfig();
  localStorage.setItem(BUILD_VERSION_KEY, remoteVersion);
  location.reload();
  await new Promise<void>(() => {});
  throw new Error('unreachable');
}

/**
 * Compares remote build SHA with localStorage marker.
 * - Mismatch + safe: clears ENV_CONFIG only, stores SHA, reloads.
 * - Mismatch + mid-onboarding / poll inside funnel: defers and notifies UI.
 */
export function ensureFreshBuild(
  options: EnsureFreshBuildOptions = {}
): Promise<FreshBuildOutcome> {
  const allowWipeReload = options.allowWipeReload !== false;

  if (!ensureFreshPromise) {
    ensureFreshPromise = (async () => {
      await unregisterServiceWorkers();

      const remoteVersion = await fetchRemoteVersion();
      if (!remoteVersion) {
        return 'skipped';
      }

      const storedVersion = localStorage.getItem(BUILD_VERSION_KEY);
      if (storedVersion === remoteVersion) {
        return 'up-to-date';
      }

      const inFunnel = await hasActiveOnboarding();
      if (inFunnel || !allowWipeReload) {
        notifyDeferred(remoteVersion);
        return 'deferred';
      }

      await applySoftUpdate(remoteVersion);
      return 'reloading';
    })().finally(() => {
      /* keep promise cached until polling resets it */
    });
  }
  return ensureFreshPromise;
}

/**
 * User explicitly accepts update mid-funnel (banner action).
 * Stores the remote SHA and reloads on the same route without wiping
 * ENV_CONFIG or Capana, so the funnel can continue.
 */
export async function forceApplyNewBuild(): Promise<void> {
  const remoteVersion = await fetchRemoteVersion();
  if (!remoteVersion) {
    return;
  }
  localStorage.setItem(BUILD_VERSION_KEY, remoteVersion);
  deferredNoticeShownFor = null;
  location.reload();
}

export interface StartVersionPollingOptions {
  intervalMs?: number;
  /** Return true when it is safe to wipe/reload (landing / fora do funil). */
  isSafeRoute?: () => boolean;
}

/**
 * Polls version.json. Só aplica wipe/reload em rotas fora do funil e sem Capana ativo.
 * Mid-funil: apenas notifica (banner). Pass intervalMs <= 0 to disable.
 */
export function startVersionPolling(
  intervalMs: number = POLL_INTERVAL_MS,
  options: StartVersionPollingOptions = {}
): () => void {
  if (!intervalMs || intervalMs <= 0) {
    return () => undefined;
  }

  const isSafeRoute = options.isSafeRoute ?? (() => isOutsideFunnelRoute());

  const timerId = window.setInterval(() => {
    ensureFreshPromise = null;
    const allowWipeReload = isSafeRoute();
    void ensureFreshBuild({ allowWipeReload });
  }, intervalMs);

  return () => window.clearInterval(timerId);
}
